import { StripeCard } from 'components/stripe-card';
import { SubscriptionPlanSummary } from 'components/subscription-plan-summary';
import { ButtonGroup, Input, RadioBox, Stack, Text } from 'components/ui';
import { addDays, format } from 'date-fns';
import { BillingCycleEnum, SubscriptionPlan } from 'lib/api/generated';
import { getSubscriptionPriceAUD } from 'lib/helpers';
import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMedia } from 'react-use';
import { DATE_FORMAT, PUBLIC_CONTACT_PAGE } from 'settings/config';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import { globalTheme } from '../../../styles/global-theme';
import { CreateOrgFormData } from '../common';
import { OrgText } from './org-details';

export interface BillingProps {
  onChangePlanClick?: VoidFunction;
  onCardCompleteStatusChange?: (complete: boolean) => void;
  setActiveBillingCycle?: Function;
  selectedBillingCycle?: BillingCycleEnum;
  price?: number | string;
  priceDescription?: string;
  hasGSTPrice?: boolean;
  selectedPlan?: SubscriptionPlan;
}

const Highlight = styled.div`
  background-color: ${globalTheme.colors.lightPink};
  padding: ${globalTheme.space[1]};
  border-radius: 20px;
`;

export const Billing: FC<BillingProps> = ({
  onChangePlanClick = () => undefined,
  onCardCompleteStatusChange,
  setActiveBillingCycle,
  selectedBillingCycle,
  selectedPlan,
}) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<CreateOrgFormData>();

  const isMobile = useMedia(`(max-width: ${breakpoints.medium})`, false);

  const details = useMemo(
    () => [
      `You will be billed ${getSubscriptionPriceAUD(
        selectedPlan?.price
      )} every ${
        selectedPlan?.billing_cycle === BillingCycleEnum.Monthly
          ? 'month + GST'
          : 'year + GST'
      }.`,
      `${
        selectedPlan?.trial_period_days
          ? `${selectedPlan?.trial_period_days} day free trial included!`
          : ''
      } Your first payment is due ${format(
        addDays(new Date(), selectedPlan?.trial_period_days || 0),
        DATE_FORMAT.long
      )}.`,
      `You can cancel your subscription at any time.`,
    ],
    [
      selectedPlan?.billing_cycle,
      selectedPlan?.price,
      selectedPlan?.trial_period_days,
    ]
  );

  return (
    <Stack space={2}>
      {setActiveBillingCycle && (
        <>
          <Stack space={0}>
            <OrgText textAlign='center' fontWeight='bold'>
              Payment frequency
            </OrgText>
            <ButtonGroup>
              <RadioBox
                label='Monthly'
                name='billingCycle'
                checked={selectedBillingCycle === BillingCycleEnum.Monthly}
                onChange={() => setActiveBillingCycle(BillingCycleEnum.Monthly)}
                data-cy='paymentFreqMonthly'
                orgFontSize={3}
                isMobile={isMobile}
              />
              <RadioBox
                label='Annual'
                name='billingCycle'
                checked={selectedBillingCycle === BillingCycleEnum.Annual}
                onChange={() => setActiveBillingCycle(BillingCycleEnum.Annual)}
                data-cy='paymentFreqAnnual'
                orgFontSize={3}
                isMobile={isMobile}
              />
            </ButtonGroup>
          </Stack>
          <Highlight>
            <OrgText
              textAlign={'center'}
              fontSize={2}
              fontWeight={'medium'}
              color={'pink'}
            >
              Save 20% on Annual subscriptions
            </OrgText>
          </Highlight>
        </>
      )}
      {selectedPlan && (
        <SubscriptionPlanSummary
          name={`${selectedPlan.name}`}
          details={details}
          onChangePlanClick={onChangePlanClick}
          priceDescription={`per ${
            selectedBillingCycle === BillingCycleEnum.Monthly ? 'month' : 'year'
          }

                `}
          hasGSTPrice={true}
          price={getSubscriptionPriceAUD(selectedPlan?.price)}
        />
      )}
      <Input
        label='Name on card'
        isRequired
        {...register('card_name')}
        state={errors.card_name && 'error'}
        message={errors.card_name?.message}
        data-cy='nameField'
        orgFontSize={3}
        isMobile={isMobile}
      />
      <StripeCard
        onComplete={() => onCardCompleteStatusChange?.(true)}
        onIncomplete={() => onCardCompleteStatusChange?.(false)}
      />
      <Text fontSize={0}>
        Looking for other options?{' '}
        <Text
          as='a'
          fontSize={0}
          href={PUBLIC_CONTACT_PAGE}
          color='blue'
          target='_blank'
          rel='noopener noreferrer'
        >
          Contact Agora
        </Text>
      </Text>
    </Stack>
  );
};
